/** Joins the parts of an activity log line: a middle dot with room on either side ("a  ·  b"); the non-breaking spaces keep HTML from collapsing it. */
export const ACTIVITY_LOG_SEPARATOR = '\u00A0 ·\u00A0 ';

const MONTH_LABELS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

const ORDINAL_SUFFIXES: Record<number, string> = { 1: 'st', 2: 'nd', 3: 'rd' };

const getOrdinalSuffix = (day: number) => day % 100 >= 11 && day % 100 <= 13 ? 'th' : ORDINAL_SUFFIXES[day % 10] ?? 'th';

const getTimeLabel = (date: Date) => `${date.getHours() % 12 || 12}:${String(date.getMinutes()).padStart(2, '0')}${date.getHours() < 12 ? 'am' : 'pm'}`;

/** An activity log's date column, in local time: "MAR" "6" "th" over "9:05am". */
export const getActivityLogWhen = (date: Date) => ({
  month: MONTH_LABELS[date.getMonth()],
  day: date.getDate(),
  ordinal: getOrdinalSuffix(date.getDate()),
  time: getTimeLabel(date),
});

/** Newest-first entries split into runs by calendar year, for an activity log's year bands. */
export const groupActivityLogEntriesByYear = <Entry extends { at: Date }>(entries: readonly Entry[]) =>
  entries.reduce<{ year: number; entries: Entry[] }[]>((groups, entry) => {
    const year = entry.at.getFullYear();
    const lastGroup = groups[groups.length - 1];
    if (lastGroup?.year === year) lastGroup.entries.push(entry);
    else groups.push({ year, entries: [entry] });
    return groups;
  }, []);

/**
 * Newest-first entries split into sections that each start at a moment: an entry belongs to the last
 * section starting at or before it (the first section takes anything earlier). Newest section first;
 * sections with no entries are left out.
 *
 * @param sections - in any order
 */
export const groupActivityLogEntriesBySection = <Section extends { startsAt: Date }, Entry extends { at: Date }>(
  sections: readonly Section[],
  entries: readonly Entry[],
): { section: Section; entries: Entry[] }[] => {
  const sectionsOldestFirst = [...sections].sort((a, b) => a.startsAt.getTime() - b.startsAt.getTime());
  const entriesBySection = new Map<Section, Entry[]>();
  for (const entry of entries) {
    const section = sectionsOldestFirst.findLast(candidate => candidate.startsAt <= entry.at) ?? sectionsOldestFirst[0];
    if (!section) continue;
    entriesBySection.set(section, [...entriesBySection.get(section) ?? [], entry]);
  }
  return sectionsOldestFirst.toReversed().flatMap(section => {
    const sectionEntries = entriesBySection.get(section);
    return sectionEntries ? [{ section, entries: sectionEntries }] : [];
  });
};

/** A calendar day in the viewer's own time zone, as a key: viewers far apart may see a late edit fall on different days. */
export const getLocalDayKey = (date: Date) => `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;

/**
 * Collapses each run of consecutive entries sharing a run key into one group of them, via `toGroup`.
 * An entry with no run key breaks any run and stands alone, as does a run of one.
 */
export const groupConsecutiveActivityLogEntries = <Entry>(
  entries: readonly Entry[],
  getRunKey: (entry: Entry) => string | undefined,
  toGroup: (run: Entry[]) => Entry,
): Entry[] => {
  const result: Entry[] = [];
  let run: Entry[] = [];
  let runKey: string | undefined;
  const flushRun = () => {
    if (run.length > 1) result.push(toGroup(run));
    else result.push(...run);
    run = [];
  };
  for (const entry of entries) {
    const key = getRunKey(entry);
    if (key === undefined || key !== runKey) flushRun();
    runKey = key;
    if (key === undefined) result.push(entry);
    else run.push(entry);
  }
  flushRun();
  return result;
};
